import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, Upload, User, Award, Globe, MapPin, Clock } from 'lucide-react';
import { useMutation, useQueryClient } from 'react-query';
import { coachingAPI, uploadAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const CoachRegistrationModal = ({ onClose, onSuccess, existingCoach }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditMode = !!existingCoach;
  
  // Initialize form data - update when existingCoach changes
  const [formData, setFormData] = useState({
    name: existingCoach?.name || user?.name || '',
    bio: existingCoach?.bio || '',
    experience_years: existingCoach?.experience_years || 0,
    specializations: existingCoach?.specializations || [],
    hourly_rate: existingCoach?.hourly_rate ? String(existingCoach.hourly_rate) : '',
    monthly_rate: existingCoach?.monthly_rate ? String(existingCoach.monthly_rate) : '',
    certifications: existingCoach?.certifications || [],
    languages: existingCoach?.languages || [],
    location: existingCoach?.location || '',
    phone: existingCoach?.phone || user?.phone || '',
    profile_image: existingCoach?.profile_image || '' // Coach-specific image only
  });

  const [isUploadingCoachImage, setIsUploadingCoachImage] = useState(false);

  const [newSpecialization, setNewSpecialization] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [newLanguage, setNewLanguage] = useState('');

  const sports = [
    'Tennis', 'Badminton', 'Squash', 'Table Tennis', 'Padel', 'Futsal', 
    'Basketball', 'Volleyball', 'Cricket', 'Football', 'Swimming', 'Gym',
    'Boxing', 'Martial Arts', 'Yoga', 'Pilates', 'Dance', 'Running'
  ];

  const languages = [
    'English', 'Urdu', 'Punjabi', 'Sindhi', 'Pashto', 'Balochi', 'Arabic', 'French', 'Spanish'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    // For rates, keep as string to preserve exact value, convert to number only on submit
    const parsedValue = (name === 'hourly_rate' || name === 'monthly_rate') ? value : value;
    setFormData(prev => ({
      ...prev,
      [name]: parsedValue
    }));
  };

  // Update form data when existingCoach changes and parse JSON arrays
  useEffect(() => {
    if (existingCoach) {
      setFormData(prev => ({
        ...prev,
        name: existingCoach.name || user?.name || prev.name,
        bio: existingCoach.bio || prev.bio,
        experience_years: existingCoach.experience_years || prev.experience_years,
        hourly_rate: existingCoach.hourly_rate ? String(existingCoach.hourly_rate) : prev.hourly_rate,
        monthly_rate: existingCoach.monthly_rate ? String(existingCoach.monthly_rate) : prev.monthly_rate,
        location: existingCoach.location || prev.location,
        phone: existingCoach.phone || user?.phone || prev.phone,
        profile_image: existingCoach.profile_image || prev.profile_image
      }));
    }

    // Parse JSON arrays from existingCoach or formData
    const parseArray = (value, defaultValue = []) => {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : defaultValue;
        } catch (e) {
          return defaultValue;
        }
      }
      return defaultValue;
    };

    // Update arrays from existingCoach if available
    if (existingCoach) {
      setFormData(prev => ({
        ...prev,
        specializations: parseArray(existingCoach.specializations, prev.specializations),
        certifications: parseArray(existingCoach.certifications, prev.certifications),
        languages: parseArray(existingCoach.languages, prev.languages)
      }));
    } else {
      // Parse formData if it's a string
      if (formData.specializations && typeof formData.specializations === 'string') {
        const parsed = parseArray(formData.specializations);
        if (Array.isArray(parsed)) {
          setFormData(prev => ({ ...prev, specializations: parsed }));
        }
      }
      if (formData.certifications && typeof formData.certifications === 'string') {
        const parsed = parseArray(formData.certifications);
        if (Array.isArray(parsed)) {
          setFormData(prev => ({ ...prev, certifications: parsed }));
        }
      }
      if (formData.languages && typeof formData.languages === 'string') {
        const parsed = parseArray(formData.languages);
        if (Array.isArray(parsed)) {
          setFormData(prev => ({ ...prev, languages: parsed }));
        }
      }
    }
  }, [existingCoach, user]);

  const addSpecialization = () => {
    if (newSpecialization && !formData.specializations.includes(newSpecialization)) {
      setFormData(prev => ({
        ...prev,
        specializations: [...(prev.specializations || []), newSpecialization]
      }));
      setNewSpecialization('');
    }
  };

  const removeSpecialization = (specialization) => {
    setFormData(prev => ({
      ...prev,
      specializations: (prev.specializations || []).filter(s => s !== specialization)
    }));
  };

  const addCertification = () => {
    if (newCertification && !formData.certifications.includes(newCertification)) {
      setFormData(prev => ({
        ...prev,
        certifications: [...(prev.certifications || []), newCertification]
      }));
      setNewCertification('');
    }
  };

  const removeCertification = (certification) => {
    setFormData(prev => ({
      ...prev,
      certifications: (prev.certifications || []).filter(c => c !== certification)
    }));
  };

  const addLanguage = () => {
    if (newLanguage && !formData.languages.includes(newLanguage)) {
      setFormData(prev => ({
        ...prev,
        languages: [...(prev.languages || []), newLanguage]
      }));
      setNewLanguage('');
    }
  };

  const removeLanguage = (language) => {
    setFormData(prev => ({
      ...prev,
      languages: (prev.languages || []).filter(l => l !== language)
    }));
  };

  const createCoachMutation = useMutation(coachingAPI.createCoachProfile, {
    onSuccess: () => {
      queryClient.invalidateQueries(['coaches']);
      queryClient.invalidateQueries(['my-coach']);
      onSuccess();
    },
    onError: (error) => {
      console.error('Error creating/updating coach profile:', error);
    }
  });

  const handleCoachImageClick = () => {
    document.getElementById('coach-image-input').click();
  };

  const handleCoachImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    setIsUploadingCoachImage(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('coachImage', file);

      const response = await uploadAPI.uploadCoachImage(formDataUpload);
      const coachImageUrl = response.data.data.coachImageUrl;

      setFormData(prev => ({
        ...prev,
        profile_image: coachImageUrl
      }));

      toast.success('Coach image uploaded successfully!');
    } catch (error) {
      console.error('Coach image upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload coach image');
    } finally {
      setIsUploadingCoachImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Ensure rates are properly formatted as numbers without precision loss
      const hourlyRateValue = formData.hourly_rate;
      const parsedHourlyRate = hourlyRateValue ? Number(hourlyRateValue) : null;
      
      const monthlyRateValue = formData.monthly_rate;
      const parsedMonthlyRate = monthlyRateValue ? Number(monthlyRateValue) : null;
      
      // Validate rates are valid positive numbers if provided
      if (parsedHourlyRate !== null && (isNaN(parsedHourlyRate) || parsedHourlyRate < 0)) {
        toast.error('Invalid hourly rate. Must be a positive number.');
        setIsSubmitting(false);
        return;
      }
      
      if (parsedMonthlyRate !== null && (isNaN(parsedMonthlyRate) || parsedMonthlyRate < 0)) {
        toast.error('Invalid monthly rate. Must be a positive number.');
        setIsSubmitting(false);
        return;
      }
      
      const submitData = {
        ...formData,
        hourly_rate: parsedHourlyRate,
        monthly_rate: parsedMonthlyRate,
        experience_years: parseInt(formData.experience_years) || 0
      };
      
      console.log('Submitting coach data:', { ...submitData });
      await createCoachMutation.mutateAsync(submitData);
    } catch (error) {
      console.error('Error creating coach profile:', error);
      toast.error(error.response?.data?.message || 'Failed to save coach profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEditMode ? 'Update Coach Profile' : 'Become a Coach'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <User className="w-5 h-5 mr-2" />
                Basic Information
              </h3>
              
              {/* Coach Image Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Coach Image *
                </label>
                <div className="flex items-center space-x-4">
                  {formData.profile_image ? (
                    <img
                      src={formData.profile_image.startsWith('http') 
                        ? formData.profile_image 
                        : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://api.eezybooking.com'}${formData.profile_image}`
                      }
                      alt="Coach"
                      className="w-24 h-24 rounded-lg object-cover border-2 border-gray-300 dark:border-gray-600"
                      key={formData.profile_image}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center bg-gray-50 dark:bg-gray-700 ${formData.profile_image ? 'hidden' : ''}`}
                    style={{ display: formData.profile_image ? 'none' : 'flex' }}
                  >
                    <Upload className="w-8 h-8 text-gray-400" />
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      id="coach-image-input"
                      accept="image/*"
                      onChange={handleCoachImageChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={handleCoachImageClick}
                      disabled={isUploadingCoachImage}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isUploadingCoachImage ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          {formData.profile_image ? 'Change Image' : 'Upload Image'}
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Upload a professional photo for your coach profile (max 2MB)
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bio *
                </label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Tell us about your coaching experience, philosophy, and what makes you unique..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Experience and Specializations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Experience & Specializations
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Years of Experience *
                  </label>
                  <input
                    type="number"
                    name="experience_years"
                    value={formData.experience_years}
                    onChange={handleChange}
                    required
                    min="0"
                    max="50"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hourly Rate (PKR)
                    </label>
                    <input
                      type="number"
                      name="hourly_rate"
                      value={formData.hourly_rate}
                      onChange={handleChange}
                      min="500"
                      max="50000"
                      step="1"
                      placeholder="e.g., 2000"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Monthly Rate (PKR)
                    </label>
                    <input
                      type="number"
                      name="monthly_rate"
                      value={formData.monthly_rate}
                      onChange={handleChange}
                      min="5000"
                      max="500000"
                      step="1"
                      placeholder="e.g., 20000"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Specializations */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sports You Coach *
                </label>
                <div className="flex gap-2 mb-2">
                  <select
                    value={newSpecialization}
                    onChange={(e) => setNewSpecialization(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select a sport</option>
                    {sports.map(sport => (
                      <option key={sport} value={sport}>{sport}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={addSpecialization}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.specializations.map((sport, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm flex items-center"
                    >
                      {sport}
                      <button
                        type="button"
                        onClick={() => removeSpecialization(sport)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Certifications */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Award className="w-5 h-5 mr-2" />
                Certifications & Qualifications
              </h3>
              
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  placeholder="e.g., Certified Tennis Coach Level 2"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
                <button
                  type="button"
                  onClick={addCertification}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2">
                {formData.certifications.map((cert, index) => (
                  <div
                    key={index}
                    className="px-3 py-2 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-lg text-sm flex items-center justify-between"
                  >
                    {cert}
                    <button
                      type="button"
                      onClick={() => removeCertification(cert)}
                      className="text-green-600 hover:text-green-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Languages */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Globe className="w-5 h-5 mr-2" />
                Languages
              </h3>
              
              <div className="flex gap-2 mb-2">
                <select
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a language</option>
                  {languages.map(lang => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={addLanguage}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.languages.map((lang, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-sm flex items-center"
                  >
                    {lang}
                    <button
                      type="button"
                      onClick={() => removeLanguage(lang)}
                      className="ml-2 text-purple-600 hover:text-purple-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Location */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Location
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  City *
                </label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select your city</option>
                  <option value="Karachi">Karachi</option>
                  <option value="Lahore">Lahore</option>
                  <option value="Islamabad">Islamabad</option>
                  <option value="Rawalpindi">Rawalpindi</option>
                  <option value="Faisalabad">Faisalabad</option>
                  <option value="Multan">Multan</option>
                  <option value="Peshawar">Peshawar</option>
                  <option value="Quetta">Quetta</option>
                  <option value="Hyderabad">Hyderabad</option>
                  <option value="Sialkot">Sialkot</option>
                </select>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                Important Disclaimer
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                By becoming a coach on our platform, you understand that:
              </p>
              <ul className="text-sm text-yellow-700 dark:text-yellow-300 mt-2 list-disc list-inside space-y-1">
                <li>You are responsible for your own services and client interactions</li>
                <li>We only provide the platform for connection, not the actual coaching services</li>
                <li>You must maintain professional standards and safety protocols</li>
                <li>Any disputes between coaches and clients are your responsibility to resolve</li>
                <li>We reserve the right to remove coaches who violate our terms of service</li>
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || formData.specializations.length === 0 || !formData.profile_image}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-blue-400 disabled:to-purple-400 text-white px-6 py-3 rounded-xl font-bold flex items-center justify-center transition-all duration-300 transform hover:scale-105 disabled:transform-none"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEditMode ? 'Updating Profile...' : 'Creating Profile...'}
                </>
              ) : (
                isEditMode ? 'Update Profile' : 'Become a Coach'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CoachRegistrationModal;
